/**
 * Liquid Glass — adapted from 21st.dev liquid-glass component.
 * Converted from TSX to JSX, "use client" removed (CRA), TypeScript annotations removed.
 *
 * Usage:
 *   1. Render <GlassFilter /> once anywhere in the DOM (App.js).
 *   2. Inside any `relative overflow-hidden` card div, drop <GlassLayers /> as the first child.
 *   3. Wrap card content in a div with `relative z-[3]` so it renders above the glass layers.
 *
 * The glass effect requires the card div to have NO opaque background-color so the
 * SVG displacement filter can distort whatever is behind the card (e.g. the dot grid).
 */

/**
 * Drop inside any card that has `relative overflow-hidden`.
 * Provides three stacked layers:
 *   z-[0] — backdrop blur + SVG displacement distortion
 *   z-[1] — white rgba tint (frosted glass fill)
 *   z-[2] — inset highlight (glass edge sheen)
 */
export function GlassLayers() {
  return (
    <>
      {/* Layer 1: Blurred, distorted backdrop */}
      <div
        className="absolute inset-0 z-0 overflow-hidden"
        style={{
          backdropFilter: 'blur(3px)',
          WebkitBackdropFilter: 'blur(3px)',
          filter: 'url(#glass-distortion)',
          isolation: 'isolate',
          borderRadius: 'inherit',
        }}
      />
      {/* Layer 2: Frosted tint */}
      <div
        className="absolute inset-0 z-[1]"
        style={{ background: 'rgba(255, 255, 255, 0.28)', borderRadius: 'inherit' }}
      />
      {/* Layer 3: Edge highlight / inset sheen */}
      <div
        className="absolute inset-0 z-[2] overflow-hidden"
        style={{
          boxShadow:
            'inset 2px 2px 1px 0 rgba(255, 255, 255, 0.55), inset -1px -1px 1px 1px rgba(255, 255, 255, 0.35)',
          borderRadius: 'inherit',
        }}
      />
    </>
  );
}

/**
 * Render once in App.js (outside routes). Hidden SVG filter definition.
 * The fractal turbulence + displacement map creates the light-refraction distortion.
 * Scale is reduced to 80 (from original 200) to suit card-sized elements.
 */
export function GlassFilter() {
  return (
    <svg style={{ display: 'none' }} aria-hidden="true">
      <defs>
        <filter
          id="glass-distortion"
          x="0%"
          y="0%"
          width="100%"
          height="100%"
          filterUnits="objectBoundingBox"
        >
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.001 0.005"
            numOctaves="1"
            seed="17"
            result="turbulence"
          />
          <feComponentTransfer in="turbulence" result="mapped">
            <feFuncR type="gamma" amplitude="1" exponent="10" offset="0.5" />
            <feFuncG type="gamma" amplitude="0" exponent="1" offset="0" />
            <feFuncB type="gamma" amplitude="0" exponent="1" offset="0.5" />
          </feComponentTransfer>
          <feGaussianBlur in="turbulence" stdDeviation="3" result="softMap" />
          <feSpecularLighting
            in="softMap"
            surfaceScale="5"
            specularConstant="1"
            specularExponent="100"
            lightingColor="white"
            result="specLight"
          >
            <fePointLight x="-200" y="-200" z="300" />
          </feSpecularLighting>
          <feComposite
            in="specLight"
            operator="arithmetic"
            k1="0"
            k2="1"
            k3="1"
            k4="0"
            result="litImage"
          />
          <feDisplacementMap
            in="SourceGraphic"
            in2="softMap"
            scale="80"
            xChannelSelector="R"
            yChannelSelector="G"
          />
        </filter>
      </defs>
    </svg>
  );
}
